import React from 'react'
import clsx from 'clsx'
import MenuItem from '@material-ui/core/MenuItem'
import MenuList from '@material-ui/core/MenuList'
import MaterialSymbol from 'components/material-symbol/material-symbol'
import EditOutlinedIcon from 'components/material-symbol/icons/Edit'
import DeleteOutlineIcon from 'components/material-symbol/icons/DeleteOutline'
import FlagIcon from 'components/material-symbol/icons/Flag'
import NotificationsOffOutlinedIcon from 'components/material-symbol/icons/NotificationsOff'
import NotificationsActiveOutlinedIcon from 'components/material-symbol/icons/NotificationsActive'
import MarkunreadMailboxIcon from 'components/material-symbol/icons/MarkunreadMailbox'
import {
  useMessageComposer,
  useMessageContext,
  useTranslationContext,
} from 'stream-chat-react'

const MESSAGE_ACTIONS = {
  quote: 'quote',
  pin: 'pin',
  markUnread: 'markUnread',
  flag: 'flag',
  mute: 'mute',
  edit: 'edit',
  delete: 'delete',
  deleteForMe: 'deleteForMe',
}

const menuIconProps = {
  fontSize: 18,
  opticalSize: 20,
  style: { paddingRight: 6 },
}

const PushPinIcon = props => (
  <MaterialSymbol
    name="push_pin"
    htmlColor="#172024"
    {...menuIconProps}
    {...props}
  />
)

const ReplyIcon = props => (
  <MaterialSymbol
    name="reply"
    htmlColor="#172024"
    {...menuIconProps}
    {...props}
  />
)

const ActionMenuItem = ({
  icon: Icon,
  children,
  onClick,
  destructive = false,
}) => (
  <MenuItem
    className={clsx('messaging-message-action-menu-item', {
      'messaging-message-action-menu-item--destructive': destructive,
    })}
    onClick={onClick}
  >
    <Icon {...menuIconProps} />
    {children}
  </MenuItem>
)

const MessagingMessageActionsBox = props => {
  const {
    className,
    getMessageActions,
    handleDelete,
    handleEdit,
    handleFlag,
    handleMarkUnread,
    handleMute,
    handlePin,
    isUserMuted,
    onClose,
    open,
    ...restDivProps
  } = props

  const { customMessageActions, message, threadList } = useMessageContext(
    'MessagingMessageActionsBox',
  )
  const messageComposer = useMessageComposer()
  const { t } = useTranslationContext('MessagingMessageActionsBox')
  const messageActions = getMessageActions()

  const handleQuote = () => {
    messageComposer.setQuotedMessage(message)
    const elements = message.parent_id
      ? document.querySelectorAll(
        '.str-chat__thread .str-chat__textarea__textarea',
      )
      : document.getElementsByClassName('str-chat__textarea__textarea')
    const textarea = elements.item(0)
    if (textarea instanceof HTMLTextAreaElement) {
      textarea.focus()
    }
  }

  const rootClassName = clsx(
    'str-chat__message-actions-box',
    'messaging-message-actions-box',
    className,
    {
      'str-chat__message-actions-box--open': open,
    },
  )

  const customActions = customMessageActions
    ? Object.keys(customMessageActions)
    : []

  const runAction = handler => event => {
    handler?.(event)
    onClose?.()
  }

  return (
    <div
      {...restDivProps}
      className={rootClassName}
      data-testid="message-actions-box"
    >
      <MenuList
        autoFocusItem
        aria-label={t('aria/Message Options')}
        className="str-chat__message-actions-list messaging-message-actions-list"
        role="menu"
      >
        {customActions.map(actionName => {
          const customHandler = customMessageActions[actionName]
          return (
            <ActionMenuItem
              key={actionName}
              icon={ReplyIcon}
              onClick={runAction(event => customHandler(message, event))}
            >
              {actionName}
            </ActionMenuItem>
          )
        })}
        {messageActions.indexOf(MESSAGE_ACTIONS.quote) > -1 && (
          <ActionMenuItem icon={ReplyIcon} onClick={runAction(handleQuote)}>
            {t('Reply')}
          </ActionMenuItem>
        )}
        {messageActions.indexOf(MESSAGE_ACTIONS.pin) > -1 &&
          !message.parent_id && (
            <ActionMenuItem icon={PushPinIcon} onClick={runAction(handlePin)}>
              {!message.pinned ? t('Pin') : t('Unpin')}
            </ActionMenuItem>
          )}
        {messageActions.indexOf(MESSAGE_ACTIONS.markUnread) > -1 &&
          !threadList &&
          !!message.id && (
            <ActionMenuItem
              icon={MarkunreadMailboxIcon}
              onClick={runAction(handleMarkUnread)}
            >
              {t('Mark as unread')}
            </ActionMenuItem>
          )}
        {messageActions.indexOf(MESSAGE_ACTIONS.flag) > -1 && (
          <ActionMenuItem icon={FlagIcon} onClick={runAction(handleFlag)}>
            {t('Flag')}
          </ActionMenuItem>
        )}
        {messageActions.indexOf(MESSAGE_ACTIONS.mute) > -1 && (
          <ActionMenuItem
            icon={
              isUserMuted()
                ? NotificationsActiveOutlinedIcon
                : NotificationsOffOutlinedIcon
            }
            onClick={runAction(handleMute)}
          >
            {isUserMuted() ? t('Unmute') : t('Mute')}
          </ActionMenuItem>
        )}
        {messageActions.indexOf(MESSAGE_ACTIONS.edit) > -1 && (
          <ActionMenuItem
            icon={EditOutlinedIcon}
            onClick={runAction(handleEdit)}
          >
            {t('Edit Message')}
          </ActionMenuItem>
        )}
        {messageActions.indexOf(MESSAGE_ACTIONS.delete) > -1 && (
          <ActionMenuItem
            destructive
            icon={DeleteOutlineIcon}
            onClick={runAction(handleDelete)}
          >
            {t('Delete')}
          </ActionMenuItem>
        )}
        {messageActions.indexOf(MESSAGE_ACTIONS.deleteForMe) > -1 &&
          !message.deleted_for_me && (
            <ActionMenuItem
              destructive
              icon={DeleteOutlineIcon}
              onClick={runAction(event =>
                handleDelete(event, { deleteForMe: true }),
              )}
            >
              {t('Delete for me')}
            </ActionMenuItem>
          )}
      </MenuList>
    </div>
  )
}

export default MessagingMessageActionsBox
